import { useEffect } from "react";

/**
 * Stops the browser's edge-swipe back/forward gesture from yanking the user out
 * of the current page (which on an SPA looks like "another app opened inside the
 * website"). Vertical scrolling and genuine horizontal scrollers / sliders /
 * carousels keep working — only page-level horizontal drags are cancelled.
 */
const EDGE_ZONE = 44; // px from either screen edge where browsers arm the gesture

function isInteractiveHorizontal(target: EventTarget | null): boolean {
  let el = target as HTMLElement | null;
  while (el && el !== document.body) {
    if (
      el.hasAttribute?.("data-allow-swipe") ||
      el.getAttribute?.("role") === "slider" ||
      el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA"
    ) {
      return true;
    }
    const style = window.getComputedStyle(el);
    const overflowX = style.overflowX;
    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      el.scrollWidth > el.clientWidth + 4
    ) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

const SwipeNavigationGuard = () => {
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let guard = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        guard = false;
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      const nearEdge =
        startX <= EDGE_ZONE || startX >= window.innerWidth - EDGE_ZONE;
      guard = nearEdge && !isInteractiveHorizontal(e.target);
    };

    const onMove = (e: TouchEvent) => {
      if (!guard || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Horizontal-dominant drag starting at a screen edge → history gesture.
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) && e.cancelable) {
        e.preventDefault();
      }
    };

    const onEnd = () => {
      guard = false;
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  return null;
};

export default SwipeNavigationGuard;
